import React, { useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import Config from '../../_config/config';
import FlipClassService from '../../_services/FlipClassService';
import './ClassReviewDashboard.css';
import LiveEngagementTracker from './LiveEngagementTracker';
import FlashQuizManager from './FlashQuizManager';
import WhiteboardManager from './WhiteboardManager';
import GroupFormationModal from './GroupFormationModal';
import CaseDiscussionManager from './CaseDiscussionManager';

interface MatchParams {
  lessonPlanId: string;
  stepId: string;
}

interface Props extends RouteComponentProps<MatchParams> {}

interface StudentData {
  id: string;
  name: string;
  enrollmentId?: number;
  pre: {
    videoMin: number;
    pdfPct: number;
    pdfPages: number;
    preQuizScore: number;
  };
  in: {
    participation: string | null;
    handsUp: number | null;
    pollCorrect: number | null;
    pollTotal?: number | null;
    groupRole: string | null;
  };
  post: {
    videoMin: number;
    pdfPct: number;
    pdfPages: number;
    practiceMin: number;
    recapViewed: boolean;
    reflection: boolean;
  };
  quiz: {
    score: number;
    items: Record<number, (number | null)[]>;
  };
}

interface StepData {
  id: number;
  title: string;
  lessonPlanName: string;
  cohort: string;
  week: string;
  targets: string[];
  resources: any;
}

interface QuizBlueprint {
  q: number;
  concept: string;
  note: string;
}

interface QuizPerformanceEntry {
  testId: number;
  testName: string;
  phase: 'pre' | 'in' | 'post';
  totalMarks: number;
  blueprint: QuizBlueprint[];
}

interface DebugInfo {
  stepId: number;
  batchId: number;
  lessonPlanId: number;
  preResources: { id: number; name: string; type: string; tags: string }[];
  preResourceIds: number[];
  postResourceIds: number[];
  pdfResourceIds: number[];
  videoResourceIds: number[];
  baselineQuizTestId: number | null;
  queries: {
    video: { description: string; usageMetricsCheck: string; fallbackQuery: string };
    pdf: { description: string; usageMetricsCheck: string; fallbackQuery: string; checkLearningAnalytics: string };
    preQuiz: { description: string; query: string; baselineQuizTestId: number | null; baselineQuizTestName?: string | null };
    submissions: { description: string; query: string };
  };
}

interface AnalyticsData {
  step: StepData;
  students: StudentData[];
  quizPerformance: QuizPerformanceEntry[];
  participationWeights?: Record<string, number>;
  debug?: DebugInfo;
}

interface ItemStat {
  idx: number;
  q: number;
  concept: string;
  note: string;
  correct: number;
  pct: number;
}

interface ComputedStats {
  n: number;
  preVideoAvg: number;
  prePdfAvg: number;
  preQuizAvg: number;
  inHandsAvg: number;
  inPollAvg: number;
  postPracticeAvg: number;
  quizAvg: number;
  itemCorrect: ItemStat[];
  hardest: ItemStat;
  easiest: ItemStat;
  quizItemAnalysis: { phase: string; testName: string; items: ItemStat[]; hardest: ItemStat; easiest: ItemStat }[];
  lowPre: StudentData[];
  lowUnderstanding: StudentData[];
  atRisk: StudentData[];
  preQuizMax: number | null;
  postQuizMax: number | null;
  pollMax: number;
  preVideoWatched: number;
  prePdfRead: number;
  preQuizAttempted: number;
  postVideoWatched: number;
  postPdfRead: number;
  postQuizAttempted: number;
  postVideoAvg: number;
  postPdfAvg: number;
  engagementPct: number;
  gainThreshold: number;
}

// AI-generated tweak interface
interface AITweak {
  id: string;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  timeEstimate?: string;
}

const THRESHOLDS = {
  VIDEO_MIN: 7,          // minutes — below this = low video engagement
  PDF_PCT: 30,           // percent — below this = low pdf engagement
  QUIZ_SCORE_RATIO: 0.5, // fraction of postQuizMax — below this = low understanding
  QUIZ_SCORE_ABS: 3,     // absolute quiz score — used where postQuizMax unavailable
};

const ClassReviewDashboard: React.FC<Props> = ({ match, history, location }) => {
  const { stepId, lessonPlanId } = match.params;
  const urlParams = new URLSearchParams(location.search);
  const batchId = urlParams.get('batchId');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [preFocus, setPreFocus] = useState('all');
  const [preSegment, setPreSegment] = useState('all');
  const [debugModal, setDebugModal] = useState<{ isOpen: boolean; type: 'video' | 'pdf' | 'quiz' | 'submissions'; title: string }>({
    isOpen: false,
    type: 'video',
    title: '',
  });
  const [showPdfInfo, setShowPdfInfo] = useState(false);
  const [showPostInfo, setShowPostInfo] = useState<string | null>(null);
  const [showQuizDetail, setShowQuizDetail] = useState<'pre' | 'post' | null>(null);

  // AI Tweaks state
  const [aiTweaks, setAiTweaks] = useState<AITweak[] | null>(null);
  const [aiTweaksLoading, setAiTweaksLoading] = useState(false);
  const [aiTweaksError, setAiTweaksError] = useState<string | null>(null);
  const [aiOverallAssessment, setAiOverallAssessment] = useState<string | null>(null);

  // Engagement tracking modals
  const [showFlashQuiz, setShowFlashQuiz] = useState(false);
  const [showEngagementTracker, setShowEngagementTracker] = useState(false);
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [showGroupFormation, setShowGroupFormation] = useState(false);
  const [showCaseDiscussion, setShowCaseDiscussion] = useState(false);

  // Post-class AI recommendations
  const [postClassRecs, setPostClassRecs] = useState<{
    recommendations: Array<{
      id: string;
      title: string;
      description: string;
      priority: "high" | "medium" | "low";
      targetGroup?: string;
      actionType?: string;
    }>;
    overallAssessment: string;
    nextClassFocus: string;
  } | null>(null);
  const [postClassRecsLoading, setPostClassRecsLoading] = useState(false);
  const [postClassRecsError, setPostClassRecsError] = useState<string | null>(null);

  // Student filter for post-class readiness categories
  const [studentFilter, setStudentFilter] = useState<'all' | 'ready' | 'borderline' | 'notReady'>('all');

  const openDebug = (type: 'video' | 'pdf' | 'quiz' | 'submissions', title: string) => {
    setDebugModal({ isOpen: true, type, title });
  };

  const generatePostClassRecommendations = async () => {
    if (!students.length || !stats) return;

    setPostClassRecsLoading(true);
    setPostClassRecsError(null);

    try {
      const n = stats.n;
      const practiced15 = students.filter(s => s.post.practiceMin >= 15).length;
      const recap = students.filter(s => s.post.recapViewed).length;
      const reflection = students.filter(s => s.post.reflection).length;
      const improved = students.filter(s => s.quiz.score - s.pre.preQuizScore >= 2).length;
      const smallGain = students.filter(s => s.quiz.score - s.pre.preQuizScore === 1).length;
      const noGain = students.filter(s => s.quiz.score - s.pre.preQuizScore <= 0).length;
      const ready = students.filter(s => s.quiz.score >= 4 && s.post.practiceMin >= 15).length;
      const borderline = students.filter(s => s.quiz.score === 3 || (s.quiz.score >= 4 && s.post.practiceMin < 15)).length;
      const notReady = students.filter(s => s.quiz.score < THRESHOLDS.QUIZ_SCORE_ABS).length;
      const notReadyNames = students.filter(s => s.quiz.score < THRESHOLDS.QUIZ_SCORE_ABS).slice(0, 10).map(s => s.name);
      const avgQuizScore = students.reduce((sum, s) => sum + s.quiz.score, 0) / n;
      const avgPracticeMin = students.reduce((sum, s) => sum + s.post.practiceMin, 0) / n;

      const response = await FlipClassService.generatePostClassRecommendations({
        stepId: Number(stepId),
        batchId: Number(batchId),
        context: {
          totalStudents: n,
          avgQuizScore,
          readyCount: ready,
          borderlineCount: borderline,
          notReadyCount: notReady,
          improvedCount: improved,
          smallGainCount: smallGain,
          noGainCount: noGain,
          avgPracticeMin,
          practicedCount: practiced15,
          recapViewedCount: recap,
          reflectionCount: reflection,
          hardestConcept: stats.hardest.concept,
          hardestConceptPctCorrect: Math.round(stats.hardest.pct * 100),
          easiestConcept: stats.easiest?.concept,
          easiestConceptPctCorrect: stats.easiest ? Math.round(stats.easiest.pct * 100) : undefined,
          notReadyStudentNames: notReadyNames,
        },
      });

      if (response.success && response.data) {
        setPostClassRecs({
          recommendations: response.data.recommendations,
          overallAssessment: response.data.overallAssessment,
          nextClassFocus: response.data.nextClassFocus,
        });
      }
    } catch (err: any) {
      console.error('Failed to generate post-class recommendations:', err);
      setPostClassRecsError(err?.message || 'Failed to generate recommendations');
    } finally {
      setPostClassRecsLoading(false);
    }
  };

  useEffect(() => {
    loadAnalyticsData();
  }, [stepId]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const getAuthToken = () => {
        const currentUser = localStorage.getItem('currentUser');
        if (currentUser && currentUser !== 'undefined') {
          try {
            const user = JSON.parse(currentUser);
            return user.token;
          } catch (error) {
            console.error('Failed to parse currentUser:', error);
          }
        }
        return null;
      };

      const token = getAuthToken();
      if (!token) {
        throw new Error('Not authenticated. Please log in.');
      }

      // Get batchId from URL query parameter (via React Router location, not window.location)
      const urlParams = new URLSearchParams(location.search);
      const batchId = urlParams.get('batchId');

      if (!batchId) {
        throw new Error('batchId is required. Please provide batchId as a query parameter.');
      }

      const apiUrl = `${Config.nodeSiteUrl}v1/flip-class/step/class-review?stepId=${stepId}&batchId=${batchId}`;

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      console.log('[ClassReviewDashboard] API Response:', result);
      console.log('[ClassReviewDashboard] Students count:', result.data?.students?.length);
      console.log('[ClassReviewDashboard] First student:', result.data?.students?.[0]);
      console.log('[ClassReviewDashboard] Debug info:', result.data?.debug);
      console.log('[ClassReviewDashboard] Pre Resource IDs:', result.data?.debug?.preResourceIds);
      console.log('[ClassReviewDashboard] PDF Resource IDs:', result.data?.debug?.pdfResourceIds);
      console.log('[ClassReviewDashboard] Queries:', result.data?.debug?.queries);

      if (result.success && result.data) {
        setData(result.data);
      } else {
        throw new Error('Invalid API response format');
      }
    } catch (err: any) {
      console.error('Failed to load analytics:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Generate AI-powered in-class tweaks
  const generateAITweaks = async (stats: ComputedStats, unprepared: number, denom: number) => {
    try {
      setAiTweaksLoading(true);
      setAiTweaksError(null);

      const getAuthToken = () => {
        const currentUser = localStorage.getItem('currentUser');
        if (currentUser && currentUser !== 'undefined') {
          try {
            const user = JSON.parse(currentUser);
            return user.token;
          } catch (error) {
            console.error('Failed to parse currentUser:', error);
          }
        }
        return null;
      };

      const token = getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const apiUrl = `${Config.nodeSiteUrl}v1/flip-class/step/generate-tweaks`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          stepId: Number(stepId),
          batchId: Number(batchId),
          segment: preSegment,
          context: {
            totalStudents: stats.n,
            unpreparedPct: Math.round((unprepared / denom) * 100),
            avgPreQuizScore: stats.preQuizAvg,
            hardestConcept: stats.hardest?.concept || 'Unknown concept',
            hardestConceptPctCorrect: Math.round((stats.hardest?.pct || 0) * 100),
            atRiskStudents: stats.atRisk?.length || 0,
            lowEngagementStudents: stats.lowPre?.length || 0,
            avgVideoMin: stats.preVideoAvg,
            avgPdfPct: stats.prePdfAvg
          }
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setAiTweaks(result.data.tweaks);
        setAiOverallAssessment(result.data.overallAssessment);
        console.log('[generateAITweaks] Generated tweaks:', result.data);
      } else {
        throw new Error(result.message || 'Failed to generate recommendations');
      }
    } catch (err: any) {
      console.error('Failed to generate AI tweaks:', err);
      setAiTweaksError(err.message || 'Failed to generate recommendations');
    } finally {
      setAiTweaksLoading(false);
    }
  };

  const avg = (arr: number[]) => (arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : 0);
  const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));
  const fmt = (n: number, d: number = 1) => (Number.isFinite(n) ? n.toFixed(d) : '—');
  const pct = (n: number) => `${Math.round(n)}%`;

  const compute = (): ComputedStats | null => {
    if (!data || !data.students) {
      console.log('[compute] No data or students:', { data, students: data?.students });
      return null;
    }

    const students = data.students;
    const n = students.length;

    console.log('[compute] Computing stats for', n, 'students');
    console.log('[compute] Sample student data:', students[0]);

    const preVideoAvg = avg(students.map(s => s.pre.videoMin));
    const prePdfAvg = avg(students.map(s => s.pre.pdfPct));
    const preQuizAttempters = students.filter(s => s.pre.preQuizScore > 0);
    const preQuizAvg = avg(preQuizAttempters.map(s => s.pre.preQuizScore));
    const inHandsAvg = avg(students.map(s => s.in.handsUp));
    const inPollAvg = avg(students.map(s => s.in.pollCorrect));
    const postPracticeAvg = avg(students.map(s => s.post.practiceMin));
    const quizAttempters = students.filter(s => s.quiz.score > 0);
    const quizAvg = avg(quizAttempters.map(s => s.quiz.score));

    // Item analysis per question (use first pre-phase test for hardest/easiest)
    const firstPreTest = (data.quizPerformance || []).find(qp => qp.phase === 'pre');
    const preTestAttempterCount = firstPreTest
      ? students.filter(s => s.quiz.items?.[firstPreTest.testId]?.some((v: number | null) => v !== null)).length
      : 0;
    const itemCorrect: ItemStat[] = firstPreTest
      ? firstPreTest.blueprint.map((q, idx) => {
          const correct = students.reduce((sum, s) => sum + ((s.quiz.items?.[firstPreTest.testId]?.[idx] === 1) ? 1 : 0), 0);
          return { idx, q: q.q, concept: q.concept, note: q.note, correct, pct: preTestAttempterCount > 0 ? correct / preTestAttempterCount : 0 };
        })
      : [];
    itemCorrect.sort((a, b) => a.pct - b.pct);

    // Dynamic quiz max from quizPerformance totalMarks (null when no quiz assigned)
    const preQuizMax = (data.quizPerformance || []).find(qp => qp.phase === 'pre')?.totalMarks ?? null;
    const postQuizMax = (data.quizPerformance || []).find(qp => qp.phase === 'post')?.totalMarks ?? null;

    // Adaptive readiness: only check thresholds for resources actually assigned
    const hasPreVideos = (data as any)?.step?.resources?.pre?.videos?.length > 0;
    const hasPrePdfs = (data as any)?.step?.resources?.pre?.pdfs?.length > 0 || (data as any)?.step?.resources?.pre?.pdfResources?.length > 0;
    const lowPre = students.filter(s => {
      if (hasPreVideos && s.pre.videoMin < THRESHOLDS.VIDEO_MIN) return true;
      if (hasPrePdfs && s.pre.pdfPct < THRESHOLDS.PDF_PCT) return true;
      if (!hasPreVideos && !hasPrePdfs) return false;
      return false;
    });
    const lowUnderstanding = postQuizMax != null ? students.filter(s => s.quiz.score < postQuizMax * THRESHOLDS.QUIZ_SCORE_RATIO) : [];
    const atRisk = postQuizMax != null ? students.filter(s => lowPre.includes(s) && s.quiz.score < postQuizMax * THRESHOLDS.QUIZ_SCORE_RATIO) : [];

    // Dynamic poll total from student engagement data (fallback to 3)
    const pollTotals = students.map(s => s.in.pollTotal || 0).filter(v => v > 0);
    const pollMax = pollTotals.length > 0 ? Math.max(...pollTotals) : 3;

    console.log('[compute] Calculated averages:', {
      preVideoAvg,
      prePdfAvg,
      preQuizAvg,
      inHandsAvg,
      inPollAvg,
      postPracticeAvg,
      quizAvg,
    });

    // Engagement counts: how many students actually engaged with each metric
    const preVideoWatched = students.filter(s => s.pre.videoMin > 0).length;
    const prePdfRead = students.filter(s => s.pre.pdfPct > 0).length;
    const preQuizAttempted = students.filter(s => s.pre.preQuizScore > 0).length;
    const postVideoWatched = students.filter(s => s.post.videoMin > 0).length;
    const postPdfRead = students.filter(s => s.post.pdfPct > 0).length;
    const postQuizAttempted = students.filter(s => s.quiz.score > 0).length;
    const postVideoAvg = avg(students.map(s => s.post.videoMin));
    const postPdfAvg = avg(students.map(s => s.post.pdfPct));

    // Weighted engagement from API (single source of truth in flip-class-constants.ts)
    const pw = data.participationWeights || { High: 100, Medium: 60, Low: 20 };
    const engagementScore = students.reduce((sum, s) => {
      const level = s.in.participation;
      if (level && pw[level] != null) return sum + pw[level] / 100;
      return sum;
    }, 0);
    const engagementPct = n > 0 ? (engagementScore / n) * 100 : 0;

    // Percentage-based gain threshold (25% of quiz max)
    const gainThreshold = postQuizMax != null ? postQuizMax * 0.25 : 0;

    // Per-phase quiz item analysis
    const defaultItem: ItemStat = { idx: 0, q: 1, concept: '', note: '', correct: 0, pct: 0 };
    const quizItemAnalysis = (data.quizPerformance || []).map(qp => {
      const attempters = students.filter(s => s.quiz.items?.[qp.testId]?.some((v: number | null) => v !== null)).length;
      const items: ItemStat[] = qp.blueprint.map((bq, idx) => {
        const correct = students.reduce((sum, s) => sum + ((s.quiz.items?.[qp.testId]?.[idx] === 1) ? 1 : 0), 0);
        return { idx, q: bq.q, concept: bq.concept, note: bq.note, correct, pct: attempters > 0 ? correct / attempters : 0 };
      });
      items.sort((a, b) => a.pct - b.pct);
      return {
        phase: qp.phase,
        testName: qp.testName,
        items,
        hardest: items[0] || defaultItem,
        easiest: items[items.length - 1] || defaultItem,
      };
    });

    return {
      n,
      preVideoAvg,
      prePdfAvg,
      preQuizAvg,
      inHandsAvg,
      inPollAvg,
      postPracticeAvg,
      quizAvg,
      itemCorrect,
      hardest: itemCorrect[0] || { idx: 0, q: 1, concept: 'N/A', note: '', correct: 0, pct: 0 },
      easiest: itemCorrect[itemCorrect.length - 1] || { idx: 0, q: 1, concept: 'N/A', note: '', correct: 0, pct: 0 },
      lowPre,
      lowUnderstanding,
      atRisk,
      preQuizMax,
      postQuizMax,
      pollMax,
      preVideoWatched,
      prePdfRead,
      preQuizAttempted,
      postVideoWatched,
      postPdfRead,
      postQuizAttempted,
      postVideoAvg,
      postPdfAvg,
      engagementPct,
      gainThreshold,
      quizItemAnalysis,
    };
  };

  // Details Modal Component
  const DebugModal = () => {
    if (!debugModal.isOpen || !data) return null;

    const debug = data.debug;
    const metricType = debugModal.type;

    const getMetricValues = () => {
      switch (metricType) {
        case 'video':
          return data.students.map(s => ({ name: s.name, value: s.pre.videoMin, unit: ' min' }));
        case 'pdf':
          return data.students.map(s => ({ name: s.name, value: s.pre.pdfPct, unit: '%' }));
        case 'quiz':
          return stats.preQuizMax != null ? data.students.map(s => ({ name: s.name, value: s.pre.preQuizScore, unit: '/' + stats.preQuizMax })) : [];
        case 'submissions':
          return [];
        default:
          return [];
      }
    };

    const metricValues = getMetricValues();

    const getSummary = () => {
      switch (metricType) {
        case 'video': {
          const avg = data.students.reduce((s, st) => s + st.pre.videoMin, 0) / data.students.length;
          const watched = data.students.filter(s => s.pre.videoMin > 0).length;
          return { items: [
            { label: 'Students watched', value: `${watched} of ${data.students.length}` },
            { label: 'Average watch time', value: `${avg.toFixed(1)} min` },
          ]};
        }
        case 'pdf': {
          const avg = data.students.reduce((s, st) => s + st.pre.pdfPct, 0) / data.students.length;
          const opened = data.students.filter(s => s.pre.pdfPct > 0).length;
          return { items: [
            { label: 'Students opened', value: `${opened} of ${data.students.length}` },
            { label: 'Average completion', value: `${avg.toFixed(0)}%` },
          ]};
        }
        case 'quiz': {
          const testName = debug?.queries?.preQuiz?.baselineQuizTestName || data?.step?.resources?.pre?.quiz?.title || 'Pre-check quiz';
          const maxScore = stats.preQuizMax ?? (data?.step?.resources?.pre?.quiz?.questions || 'N/A');
          const avg = data.students.reduce((s, st) => s + st.pre.preQuizScore, 0) / data.students.length;
          const attempted = data.students.filter(s => s.pre.preQuizScore > 0).length;
          return { items: [
            { label: 'Quiz', value: String(testName) },
            { label: 'Max score', value: String(maxScore) },
            { label: 'Students attempted', value: `${attempted} of ${data.students.length}` },
            { label: 'Average score', value: `${avg.toFixed(1)}/${maxScore}` },
          ]};
        }
        case 'submissions': {
          const sub = data?.step?.resources?.pre?.submissions;
          return { items: [
            { label: 'Students submitted', value: `${sub?.studentsSubmitted || 0} of ${sub?.totalStudents || 0}` },
            { label: 'Graded', value: String(sub?.gradedCount || 0) },
            { label: 'Average score', value: sub?.avgScore !== null && sub?.avgScore !== undefined ? `${sub.avgScore}%` : 'N/A' },
          ]};
        }
        default: return { items: [] };
      }
    };

    const summary = getSummary();

    // Resources for video/pdf — filter by debug resource ID lists (TypeId-based) rather than type name strings
    const resources = (metricType === 'video' || metricType === 'pdf') ? (debug?.preResources || []) : [];
    const videoIdSet = new Set(debug?.videoResourceIds || []);
    const pdfIdSet = new Set(debug?.pdfResourceIds || []);
    const filteredResources = metricType === 'video'
      ? resources.filter(r => videoIdSet.has(r.id))
      : metricType === 'pdf'
      ? resources.filter(r => pdfIdSet.has(r.id))
      : resources;

    return (
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        onClick={() => setDebugModal({ ...debugModal, isOpen: false })}
      >
        <div
          style={{ backgroundColor: 'white', borderRadius: '10px', padding: '24px', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto', width: '90%' }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>{debugModal.title}</h3>
            <button onClick={() => setDebugModal({ ...debugModal, isOpen: false })} style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
          </div>

          {/* Summary */}
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'var(--surface-page)', borderRadius: '8px', border: '1px solid var(--border-default)' }}>
            {summary.items.map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-muted)' }}>{item.label}</span>
                <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{item.value}</span>
              </div>
            ))}
          </div>

          {/* Resources table for video/pdf */}
          {filteredResources.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: 'var(--text-body)' }}>Assigned resources</h4>
              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 4px', color: 'var(--text-muted)', fontWeight: 500 }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '6px 4px', color: 'var(--text-muted)', fontWeight: 500 }}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResources.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '6px 4px' }}>{r.name}</td>
                      <td style={{ padding: '6px 4px', color: 'var(--text-muted)' }}>{r.type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Per-Student breakdown */}
          {metricValues.length > 0 && (
            <div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: 'var(--text-body)' }}>Per student</h4>
              <div style={{ maxHeight: '250px', overflow: 'auto' }}>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 4px', color: 'var(--text-muted)', fontWeight: 500 }}>Student</th>
                      <th style={{ textAlign: 'right', padding: '6px 4px', color: 'var(--text-muted)', fontWeight: 500 }}>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metricValues.map((m, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '6px 4px' }}>{m.name}</td>
                        <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 500 }}>{m.value}{m.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // PDF Info Modal - explains what the metrics mean
  const PdfInfoModal = () => {
    if (!showPdfInfo) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
        onClick={() => setShowPdfInfo(false)}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '480px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>ⓘ PDF Metrics Explained</h3>
            <button
              onClick={() => setShowPdfInfo(false)}
              style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--text-muted)' }}
            >
              ×
            </button>
          </div>

          <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f7ff', borderRadius: '8px' }}>
              <strong style={{ color: 'var(--text-body)' }}>Students opened</strong>
              <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
                Number of students who viewed the PDF at least once
              </p>
            </div>

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0fff4', borderRadius: '8px' }}>
              <strong style={{ color: 'var(--text-body)' }}>Avg reached page</strong>
              <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
                Average of the <em>furthest page</em> each student scrolled to. This shows reading depth, not total pages viewed.
              </p>
            </div>

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fffaf0', borderRadius: '8px' }}>
              <strong style={{ color: 'var(--text-body)' }}>Progress bar</strong>
              <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
                Percentage of students who opened this PDF (access rate)
              </p>
            </div>

            <div style={{ padding: '12px', backgroundColor: '#fff5f5', borderRadius: '8px' }}>
              <strong style={{ color: 'var(--text-body)' }}>⚠️ No engagement</strong>
              <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
                This PDF hasn't been opened by any student yet. Consider reminding students or checking if it's accessible.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Post-class Info Modal - explains what the post-class metrics mean
  const PostClassInfoModal = () => {
    if (!showPostInfo) return null;

    const titles: Record<string, string> = {
      activities: 'Post Activities — How They Are Calculated',
      gains: 'Learning Gains — How They Are Calculated',
      scores: 'Quiz Scores — How They Are Calculated',
    };

    const content: Record<string, JSX.Element> = {
      activities: (
        <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f7ff', borderRadius: '8px' }}>
            <strong style={{ color: 'var(--text-body)' }}>Practice (15+ min)</strong>
            <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
              Count of students whose <em>total time spent</em> on all post-class resources is 15 minutes or more. Time is summed from individual learning activity records.
            </p>
          </div>
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0fff4', borderRadius: '8px' }}>
            <strong style={{ color: 'var(--text-body)' }}>Recap video</strong>
            <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
              Count of students who watched any post-class video for at least some time (any recorded time &gt; 0).
            </p>
          </div>
          <div style={{ padding: '12px', backgroundColor: '#fffaf0', borderRadius: '8px' }}>
            <strong style={{ color: 'var(--text-body)' }}>Reflection</strong>
            <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
              Count of students who submitted a reflection for any post-class resource (submission status is not Draft).
            </p>
          </div>
        </div>
      ),
      gains: (
        <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
          <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f0f7ff', borderRadius: '8px' }}>
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>
              Learning gains compare two quiz scores per student:<br />
              <strong>Pre-check score</strong> — from the baseline (pre-tagged) test<br />
              <strong>Post-check score</strong> — from the endline (post-tagged) test<br />
              Both scores are the sum of marks from the test's parent section in the student result.
            </p>
          </div>
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0fff4', borderRadius: '8px' }}>
            <strong style={{ color: 'var(--text-body)' }}>Improved (&ge; +2)</strong>
            <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
              Post score &minus; Pre score &ge; 2
            </p>
          </div>
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fffaf0', borderRadius: '8px' }}>
            <strong style={{ color: 'var(--text-body)' }}>Small gain (+1)</strong>
            <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
              Post score &minus; Pre score = 1
            </p>
          </div>
          <div style={{ padding: '12px', backgroundColor: '#fff5f5', borderRadius: '8px' }}>
            <strong style={{ color: 'var(--text-body)' }}>No gain</strong>
            <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
              Post score &minus; Pre score &le; 0 (no improvement or regression)
            </p>
          </div>
        </div>
      ),
      scores: (
        <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f7ff', borderRadius: '8px' }}>
            <strong style={{ color: 'var(--text-body)' }}>Pre-check (Baseline)</strong>
            <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
              Average of each student's baseline quiz score. The score is the sum of marks from the parent section of the pre-tagged test, divided by number of students.
            </p>
          </div>
          <div style={{ padding: '12px', backgroundColor: '#f0fff4', borderRadius: '8px' }}>
            <strong style={{ color: 'var(--text-body)' }}>Post-check (Endline)</strong>
            <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
              Average of each student's endline quiz score. The score is the sum of marks from the parent section of the post-tagged test, divided by number of students.
            </p>
          </div>
        </div>
      ),
    };

    return (
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}
        onClick={() => setShowPostInfo(null)}
      >
        <div
          style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '480px', width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>{titles[showPostInfo] || 'Info'}</h3>
            <button
              onClick={() => setShowPostInfo(null)}
              style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--text-muted)' }}
            >
              &times;
            </button>
          </div>
          {content[showPostInfo] || null}
        </div>
      </div>
    );
  };

  // Quiz Detail Modal - shows test name, student scores, and multi-test warning
  const QuizDetailModal = () => {
    if (!showQuizDetail || !data) return null;

    const phase = showQuizDetail;
    const phaseLabel = phase === 'pre' ? 'Pre-Quiz' : 'Post-Quiz';
    const quizInfo = phase === 'pre' ? data.step.resources.pre.quiz : data.step.resources.post.quiz;
    const phaseTests = (data.quizPerformance || []).filter(qp => qp.phase === phase);
    const st = compute();

    const maxScore = phase === 'pre' ? st?.preQuizMax : st?.postQuizMax;
    const studentScores = data.students.map(s => ({
      name: s.name,
      score: phase === 'pre' ? s.pre.preQuizScore : s.quiz.score,
    })).sort((a, b) => {
      if (a.score > 0 && b.score <= 0) return -1;
      if (a.score <= 0 && b.score > 0) return 1;
      return b.score - a.score;
    });

    const attempted = studentScores.filter(s => s.score > 0).length;

    return (
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}
        onClick={() => setShowQuizDetail(null)}
      >
        <div
          style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '520px', width: '90%', maxHeight: '80vh', overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>{phaseLabel} Scores</h3>
            <button
              onClick={() => setShowQuizDetail(null)}
              style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--text-muted)' }}
            >
              &times;
            </button>
          </div>

          {quizInfo && (
            <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: 'var(--surface-page)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-body)' }}>
              Test: <strong>{quizInfo.title}</strong> &middot; Max marks: {maxScore ?? '—'}
            </div>
          )}

          {phaseTests.length > 1 && (
            <div style={{ marginBottom: '12px', padding: '10px 12px', backgroundColor: '#fffaf0', borderRadius: '8px', border: '1px solid #fed7aa', fontSize: '12px', color: 'var(--status-warning-text)' }}>
              {phaseTests.length} tests are tagged <strong>{phase === 'pre' ? 'Pre-Class' : 'Post-Class'}</strong>. Only the first test (<strong>{quizInfo?.title || 'unknown'}</strong>) is shown in the summary card. Other tests: {phaseTests.filter((_, i) => i > 0).map(t => t.testName).join(', ')}.
            </div>
          )}

          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
            {attempted} of {studentScores.length} attempted
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-default)' }}>
                <th style={{ textAlign: 'left', padding: '8px 6px', color: 'var(--text-muted)', fontWeight: 600 }}>Student</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', color: 'var(--text-muted)', fontWeight: 600 }}>Score</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', color: 'var(--text-muted)', fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {studentScores.map((s, i) => {
                const pctScore = maxScore && maxScore > 0 ? s.score / maxScore : 0;
                const statusColor = s.score <= 0 ? '#94a3b8' : pctScore >= 0.5 ? '#10b981' : '#f59e0b';
                const statusText = s.score <= 0 ? 'Not attempted' : pctScore >= 0.5 ? 'Pass' : 'Needs review';
                return (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '8px 6px', color: 'var(--text-strong)' }}>{s.name}</td>
                    <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: s.score <= 0 ? '#94a3b8' : '#1e293b' }}>
                      {s.score > 0 ? `${s.score}/${maxScore}` : '—'}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: statusColor }}>
                      {statusText}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Helper Components
  const BarRow: React.FC<{ label: string; valueText: string; frac: number }> = ({ label, valueText, frac }) => (
    <div style={{ margin: '10px 0' }}>
      <div className="row">
        <div>
          <div className="name">{label}</div>
          <div className="meta">{valueText}</div>
        </div>
        <div className="tag">{pct(frac * 100)}</div>
      </div>
      <div className="bar">
        <span style={{ width: `${Math.round(frac * 100)}%` }}></span>
      </div>
    </div>
  );

  const Insight: React.FC<{ dotClass: string; title: string; desc: string }> = ({ dotClass, title, desc }) => (
    <div className="insight">
      <div className={`dot ${dotClass}`}></div>
      <div>
        <div className="t">{title}</div>
        <div className="d">{desc}</div>
      </div>
    </div>
  );

  const DonutChart: React.FC<{ questionNum: number; correct: number; wrong: number; notAttempted: number }> = ({
    questionNum,
    correct,
    wrong,
    notAttempted
  }) => {
    const total = correct + wrong + notAttempted;
    const radius = 45;
    const circumference = 2 * Math.PI * radius;

    const correctPct = total > 0 ? correct / total : 0;
    const wrongPct = total > 0 ? wrong / total : 0;
    const notAttemptedPct = total > 0 ? notAttempted / total : 0;

    const correctDash = correctPct * circumference;
    const wrongDash = wrongPct * circumference;
    const notAttemptedDash = notAttemptedPct * circumference;

    const correctOffset = 0;
    const wrongOffset = -correctDash;
    const notAttemptedOffset = -(correctDash + wrongDash);

    return (
      <div className="donut-chart-card">
        <svg className="donut-svg" viewBox="0 0 100 100">
          <circle
            className="donut-circle donut-bg"
            cx="50"
            cy="50"
            r={radius}
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset="0"
          />
          <circle
            className="donut-circle donut-correct"
            cx="50"
            cy="50"
            r={radius}
            strokeDasharray={`${correctDash} ${circumference}`}
            strokeDashoffset={correctOffset}
          />
          <circle
            className="donut-circle donut-wrong"
            cx="50"
            cy="50"
            r={radius}
            strokeDasharray={`${wrongDash} ${circumference}`}
            strokeDashoffset={wrongOffset}
          />
          <circle
            className="donut-circle donut-not-attempted"
            cx="50"
            cy="50"
            r={radius}
            strokeDasharray={`${notAttemptedDash} ${circumference}`}
            strokeDashoffset={notAttemptedOffset}
          />
        </svg>
        <div className="donut-label">Q{questionNum}</div>
        <div className="donut-stats">
          <div className="donut-stat-row">
            <div className="donut-stat-dot correct"></div>
            <span className="donut-stat-text">Correct: {correct}</span>
          </div>
          <div className="donut-stat-row">
            <div className="donut-stat-dot wrong"></div>
            <span className="donut-stat-text">Wrong: {wrong}</span>
          </div>
          <div className="donut-stat-row">
            <div className="donut-stat-dot not-attempted"></div>
            <span className="donut-stat-text">Not Attempted: {notAttempted}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderQuizDonuts = (phase: 'pre' | 'in' | 'post') => {
    if (!data) return null;
    const tests = (data.quizPerformance || []).filter(qp => qp.phase === phase);
    if (tests.length === 0) return null;
    return tests.map(test => (
      <div className="card" key={test.testId}>
        <h2>
          {test.testName}
          <span className="hint">Question-wise breakdown</span>
        </h2>
        <div className="donut-charts-grid">
          {test.blueprint.map((q, idx) => {
            const correct = students.reduce((sum, s) =>
              sum + ((s.quiz.items?.[test.testId]?.[idx] === 1) ? 1 : 0), 0);
            const wrong = students.reduce((sum, s) =>
              sum + ((s.quiz.items?.[test.testId]?.[idx] === 0) ? 1 : 0), 0);
            const notAttempted = stats.n - correct - wrong;
            return <DonutChart key={q.q} questionNum={q.q} correct={correct} wrong={wrong} notAttempted={notAttempted} />;
          })}
        </div>
        <div className="donut-summary">
          Total Questions: {test.blueprint.length} | Total Students: {stats.n}
        </div>
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="class-review-dashboard">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '16px', color: 'var(--text-muted)' }}>Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="class-review-dashboard">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '16px', color: 'var(--status-danger)', marginBottom: '12px' }}>Error: {error}</div>
          <button className="btn" onClick={loadAnalyticsData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const stats = compute();
  if (!stats) {
    return null;
  }

  const students = data.students;

  return (
    <div className="class-review-dashboard">
      <DebugModal />
      <PdfInfoModal />
      <PostClassInfoModal />
      <QuizDetailModal />
      <div className="app">
        {/* Topbar */}
        <div className="topbar">
          <div className="titlewrap">
            <div className="fc-icon">FC</div>
            <div>
              <h1>Step Analytics - Flipped Class (Pre, In, Post)</h1>
              <div className="subtitle">
                {data.step.lessonPlanName} • {data.step.title} • {data.step.cohort} • {data.step.week}
              </div>
            </div>
          </div>

          <div className="actions">
            <div className="chip">
              Students: <b>{stats.n}</b>
            </div>
            <div className="chip">
              Pre completion: <b>{Math.round(((stats.n - stats.lowPre.length) / stats.n) * 100)}%</b>
            </div>
            <div className="chip">
              Quiz avg: <b>{stats.postQuizMax != null ? `${fmt(stats.quizAvg, 2)}/${stats.postQuizMax}` : 'N/A'}</b>
            </div>
            <button
              className="btn grade-ai"
              onClick={() => {
                window.open(`/ai-grading-studio?stepId=${stepId}&batchId=${batchId}`, '_blank');
              }}
              title="Open AI Grading Studio for this step"
            >
              Grading Studio
            </button>
            <button className="btn" onClick={() => {
              const rows = students.map(s => ({
                'Student ID': s.id,
                'Name': s.name,
                'Video (min)': s.pre.videoMin,
                'PDF (%)': s.pre.pdfPct,
                'Pre-Quiz': s.pre.preQuizScore,
                'Participation': s.in.participation || '',
                'Hands Up': s.in.handsUp ?? '',
                'Poll Correct': s.in.pollCorrect ?? '',
                'Group Role': s.in.groupRole || '',
                'Practice (min)': s.post.practiceMin,
                'Recap Viewed': s.post.recapViewed ? 'Yes' : 'No',
                'Reflection': s.post.reflection ? 'Yes' : 'No',
                'Final Quiz': s.quiz.score,
              }));
              const headers = Object.keys(rows[0]);
              const csvContent = [
                headers.join(','),
                ...rows.map(r => headers.map(h => {
                  const val = String((r as any)[h]);
                  return val.includes(',') ? `"${val}"` : val;
                }).join(','))
              ].join('\n');
              const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `class-review-step${stepId}-batch${batchId}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}>
              Export CSV
            </button>
          </div>
        </div>

        {/* Layout */}
        <div className="layout">
          {/* Sidebar */}
          <aside className="side">
            <div className="navtitle">Views</div>
            <div className="nav">
              <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>
                Overview <span className="pill">Plan next class</span>
              </button>
              <button className={activeTab === 'pre' ? 'active' : ''} onClick={() => setActiveTab('pre')}>
                Pre-class <span className="pill">Tune in-class</span>
              </button>
              <button className={activeTab === 'in' ? 'active' : ''} onClick={() => setActiveTab('in')}>
                In-class <span className="pill">Delivery</span>
              </button>
              <button className={activeTab === 'post' ? 'active' : ''} onClick={() => setActiveTab('post')}>
                Post-class <span className="pill">Reinforce</span>
              </button>
              <button className={activeTab === 'students' ? 'active' : ''} onClick={() => setActiveTab('students')}>
                Student Table <span className="pill">Intervene</span>
              </button>
            </div>

          </aside>

          {/* Main Content */}
          <main className="content">
            {activeTab === 'overview' && (
              <div className="grid">
                {/* Three-Phase Summary */}
                <div className="card">
                  <h2>
                    Class Progress Overview
                    <span className="hint">Pre → In → Post class journey</span>
                  </h2>
                  <div className="three-phase-summary">
                    {/* Pre-Class */}
                    <div className="phase-column pre clickable" onClick={() => setActiveTab('pre')}>
                      <div className="phase-header">
                        <div className="phase-icon">📖</div>
                        <div className="phase-title">Pre-Class</div>
                        <div className="phase-subtitle">Preparation Phase</div>
                      </div>
                      <div className="phase-metrics">
                        <div className="phase-metric">
                          <span className="phase-metric-label">Video Minutes (avg)</span>
                          {data?.step?.resources?.pre?.videos?.length ? (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value">{fmt(stats.preVideoAvg, 1)}m</span>
                              <span className="phase-metric-sub">{stats.preVideoWatched} of {stats.n} watched</span>
                            </div>
                          ) : (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value" style={{ color: 'var(--text-faint)' }}>N/A</span>
                              <span className="phase-metric-sub">No videos assigned</span>
                            </div>
                          )}
                        </div>
                        <div className="phase-metric">
                          <span className="phase-metric-label">PDF Completion (avg)</span>
                          {data?.step?.resources?.pre?.pdfResources?.length ? (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value">{fmt(stats.prePdfAvg, 0)}%</span>
                              <span className="phase-metric-sub">{stats.prePdfRead} of {stats.n} read</span>
                            </div>
                          ) : (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value" style={{ color: 'var(--text-faint)' }}>N/A</span>
                              <span className="phase-metric-sub">No PDFs assigned</span>
                            </div>
                          )}
                        </div>
                        <div className="phase-metric" style={{ cursor: data?.step?.resources?.pre?.quiz ? 'pointer' : undefined }} onClick={(e) => { if (data?.step?.resources?.pre?.quiz) { e.stopPropagation(); setShowQuizDetail('pre'); } }}>
                          <span className="phase-metric-label">Pre-Quiz Avg {data?.step?.resources?.pre?.quiz && <span className="info-icon" title="Click for details">&#9432;</span>}</span>
                          {data?.step?.resources?.pre?.quiz ? (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value">{fmt(stats.preQuizAvg, 2)}/{stats.preQuizMax}</span>
                              <span className="phase-metric-sub">{stats.preQuizAttempted} of {stats.n} attempted</span>
                            </div>
                          ) : (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value" style={{ color: 'var(--text-faint)' }}>N/A</span>
                              <span className="phase-metric-sub">No pre-quiz assigned</span>
                            </div>
                          )}
                        </div>
                        <div className="phase-metric">
                          <span className="phase-metric-label">AI Submissions</span>
                          {data?.step?.resources?.pre?.submissions?.submittableResourceCount ? (
                            <span className="phase-metric-value">
                              {data.step.resources.pre.submissions.studentsSubmitted}/{data.step.resources.pre.submissions.totalStudents}
                            </span>
                          ) : (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value" style={{ color: 'var(--text-faint)' }}>N/A</span>
                              <span className="phase-metric-sub">No AI assignments</span>
                            </div>
                          )}
                        </div>
                        <div className="phase-metric">
                          <span className="phase-metric-label">Readiness</span>
                          <span
                            className="phase-metric-value"
                            style={{
                              color:
                                ((stats.n - stats.lowPre.length) / stats.n) * 100 >= 70
                                  ? '#10b981'
                                  : ((stats.n - stats.lowPre.length) / stats.n) * 100 >= 50
                                  ? '#f59e0b'
                                  : '#ef4444'
                            }}
                          >
                            {Math.round(((stats.n - stats.lowPre.length) / stats.n) * 100)}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* In-Class */}
                    <div className="phase-column in clickable" onClick={() => setActiveTab('in')}>
                      <div className="phase-header">
                        <div className="phase-icon">👩‍🏫</div>
                        <div className="phase-title">In-Class</div>
                        <div className="phase-subtitle">Live Engagement</div>
                      </div>
                      <div className="phase-metrics">
                        <div className="phase-metric">
                          <span className="phase-metric-label">Hands-up Avg</span>
                          <span className="phase-metric-value">{fmt(stats.inHandsAvg, 1)}</span>
                        </div>
                        <div className="phase-metric">
                          <span className="phase-metric-label">Poll Correct</span>
                          <span className="phase-metric-value">{fmt(stats.inPollAvg, 1)}/{stats.pollMax}</span>
                        </div>
                        <div className="phase-metric">
                          <span className="phase-metric-label">High Participation</span>
                          <span className="phase-metric-value">
                            {students.filter(s => s.in.participation === 'High').length} / {stats.n}
                          </span>
                        </div>
                        <div className="phase-metric">
                          <span className="phase-metric-label">Engagement</span>
                          <span
                            className="phase-metric-value"
                            style={{
                              color:
                                stats.engagementPct >= 60
                                  ? '#10b981'
                                  : stats.engagementPct >= 40
                                  ? '#f59e0b'
                                  : '#ef4444'
                            }}
                          >
                            {Math.round(stats.engagementPct)}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Post-Class */}
                    <div className="phase-column post clickable" onClick={() => setActiveTab('post')}>
                      <div className="phase-header">
                        <div className="phase-icon">📝</div>
                        <div className="phase-title">Post-Class</div>
                        <div className="phase-subtitle">Reinforcement</div>
                      </div>
                      <div className="phase-metrics">
                        <div className="phase-metric">
                          <span className="phase-metric-label">Video Minutes (avg)</span>
                          {data?.step?.resources?.post?.videos?.length ? (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value">{fmt(stats.postVideoAvg, 1)}m</span>
                              <span className="phase-metric-sub">{stats.postVideoWatched} of {stats.n} watched</span>
                            </div>
                          ) : (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value" style={{ color: 'var(--text-faint)' }}>N/A</span>
                              <span className="phase-metric-sub">No videos assigned</span>
                            </div>
                          )}
                        </div>
                        <div className="phase-metric">
                          <span className="phase-metric-label">PDF Completion (avg)</span>
                          {data?.step?.resources?.post?.pdfResources?.length ? (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value">{fmt(stats.postPdfAvg, 0)}%</span>
                              <span className="phase-metric-sub">{stats.postPdfRead} of {stats.n} read</span>
                            </div>
                          ) : (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value" style={{ color: 'var(--text-faint)' }}>N/A</span>
                              <span className="phase-metric-sub">No PDFs assigned</span>
                            </div>
                          )}
                        </div>
                        <div className="phase-metric" style={{ cursor: data?.step?.resources?.post?.quiz ? 'pointer' : undefined }} onClick={(e) => { if (data?.step?.resources?.post?.quiz) { e.stopPropagation(); setShowQuizDetail('post'); } }}>
                          <span className="phase-metric-label">Post-Quiz Avg {data?.step?.resources?.post?.quiz && <span className="info-icon" title="Click for details">&#9432;</span>}</span>
                          {data?.step?.resources?.post?.quiz ? (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value">{fmt(stats.quizAvg, 2)}/{stats.postQuizMax}</span>
                              <span className="phase-metric-sub">{stats.postQuizAttempted} of {stats.n} attempted</span>
                            </div>
                          ) : (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value" style={{ color: 'var(--text-faint)' }}>N/A</span>
                              <span className="phase-metric-sub">No post-quiz assigned</span>
                            </div>
                          )}
                        </div>
                        <div className="phase-metric">
                          <span className="phase-metric-label">AI Submissions</span>
                          {data?.step?.resources?.post?.submissions?.submittableResourceCount ? (
                            <span className="phase-metric-value">
                              {data.step.resources.post.submissions.studentsSubmitted}/{data.step.resources.post.submissions.totalStudents}
                            </span>
                          ) : (
                            <div className="phase-metric-right">
                              <span className="phase-metric-value" style={{ color: 'var(--text-faint)' }}>N/A</span>
                              <span className="phase-metric-sub">No AI assignments</span>
                            </div>
                          )}
                        </div>
                        <div className="phase-metric">
                          <span className="phase-metric-label">Quiz Completion</span>
                          <span
                            className="phase-metric-value"
                            style={{
                              color:
                                (stats.postQuizAttempted / stats.n) * 100 >= 70
                                  ? '#10b981'
                                  : (stats.postQuizAttempted / stats.n) * 100 >= 50
                                  ? '#f59e0b'
                                  : '#ef4444'
                            }}
                          >
                            {Math.round((stats.postQuizAttempted / stats.n) * 100)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Baseline vs End-line */}
                {(stats.preQuizMax != null || stats.postQuizMax != null) ? (
                <div className="card">
                  <h2>
                    Learning Gains: Baseline vs End-line Quiz
                    <span className="hint">Pre-check quiz → Final quiz improvement</span>
                  </h2>
                  <div className="baseline-endline-container">
                    <div className="baseline-quiz">
                      <div className="quiz-title">
                        Baseline Quiz
                        <br />
                        (Pre-Check)
                      </div>
                      {stats.preQuizMax != null ? (
                        <>
                          <svg className="quiz-donut-large donut-svg" viewBox="0 0 100 100">
                            <circle className="donut-circle" cx="50" cy="50" r="45" stroke="#e5e7eb" strokeWidth="10" fill="none" />
                            <circle
                              className="donut-circle"
                              cx="50"
                              cy="50"
                              r="45"
                              stroke="var(--brand-primary)"
                              strokeWidth="10"
                              fill="none"
                              strokeDasharray={`${(stats.preQuizAvg / stats.preQuizMax) * 283} 283`}
                              strokeDashoffset="0"
                              transform="rotate(-90 50 50)"
                              style={{ transition: 'stroke-dasharray 1.5s ease-out' }}
                            />
                          </svg>
                          <div>
                            <span className="quiz-score">{fmt(stats.preQuizAvg, 2)}</span>
                            <span className="quiz-outof">/{stats.preQuizMax}</span>
                          </div>
                        </>
                      ) : (
                        <div style={{ padding: '20px 0', color: 'var(--text-faint)', textAlign: 'center' }}>
                          <span style={{ fontSize: '1.5rem', fontWeight: 600 }}>N/A</span>
                          <br />
                          <span style={{ fontSize: '0.85rem' }}>No pre-quiz assigned</span>
                        </div>
                      )}
                    </div>

                    <div className="improvement-arrow">
                      <div className="improvement-icon">→</div>
                      <div className="improvement-text">Learning Gain</div>
                      {stats.preQuizMax != null && stats.postQuizMax != null ? (
                        <>
                          <div className="improvement-value">
                            {stats.quizAvg - stats.preQuizAvg >= 0 ? '+' : ''}
                            {fmt(stats.quizAvg - stats.preQuizAvg, 2)}
                          </div>
                          <div className="improvement-students">
                            {(() => {
                              const both = students.filter(s => s.pre.preQuizScore > 0 && s.quiz.score > 0);
                              const gt = stats.gainThreshold;
                              const improved = both.filter(s => s.quiz.score - s.pre.preQuizScore >= gt).length;
                              const declined = both.filter(s => s.pre.preQuizScore - s.quiz.score >= gt).length;
                              const maintained = both.length - improved - declined;
                              return (
                                <>
                                  📈 {improved} improved<br />
                                  ➡️ {maintained} maintained<br />
                                  {declined > 0 && `📉 ${declined} declined`}
                                </>
                              );
                            })()}
                          </div>
                        </>
                      ) : (
                        <div className="improvement-value" style={{ color: 'var(--text-faint)', fontSize: '0.85rem' }}>
                          Need both quizzes to compute
                        </div>
                      )}
                    </div>

                    <div className="endline-quiz">
                      <div className="quiz-title">
                        End-line Quiz
                        <br />
                        (Final Assessment)
                      </div>
                      {stats.postQuizMax != null ? (
                        <>
                          <svg className="quiz-donut-large donut-svg" viewBox="0 0 100 100">
                            <circle className="donut-circle" cx="50" cy="50" r="45" stroke="#e5e7eb" strokeWidth="10" fill="none" />
                            <circle
                              className="donut-circle"
                              cx="50"
                              cy="50"
                              r="45"
                              stroke="var(--status-success)"
                              strokeWidth="10"
                              fill="none"
                              strokeDasharray={`${(stats.quizAvg / stats.postQuizMax) * 283} 283`}
                              strokeDashoffset="0"
                              transform="rotate(-90 50 50)"
                              style={{ transition: 'stroke-dasharray 1.5s ease-out', transitionDelay: '0.3s' }}
                            />
                          </svg>
                          <div>
                            <span className="quiz-score">{fmt(stats.quizAvg, 2)}</span>
                            <span className="quiz-outof">/{stats.postQuizMax}</span>
                          </div>
                        </>
                      ) : (
                        <div style={{ padding: '20px 0', color: 'var(--text-faint)', textAlign: 'center' }}>
                          <span style={{ fontSize: '1.5rem', fontWeight: 600 }}>N/A</span>
                          <br />
                          <span style={{ fontSize: '0.85rem' }}>No post-quiz assigned</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                ) : null}

                {/* Quiz donut charts moved to phase tabs (pre/in/post) */}

                {/* Step Summary with KPIs */}
                <div className="card">
                  <h2>
                    Step summary
                    <span className="hint">Cross-phase view (pre, in, post) to plan the next step</span>
                  </h2>

                  <div className="kpis">
                    <div className="kpi">
                      <div className="label">Pre-class readiness</div>
                      <div className="value">{Math.round(((stats.n - stats.lowPre.length) / stats.n) * 100)}%</div>
                      <div className="sub">{stats.lowPre.length} students flagged for low engagement</div>
                    </div>
                    <div className="kpi">
                      <div className="label">In-class engagement</div>
                      <div className="value">{Math.round(stats.engagementPct)}%</div>
                      <div className="sub">Weighted: High + Medium + Low participation</div>
                    </div>
                    <div className="kpi">
                      <div className="label">Post-quiz average</div>
                      <div className="value">{stats.postQuizMax != null ? `${fmt(stats.quizAvg, 2)}/${stats.postQuizMax}` : 'N/A'}</div>
                      <div className="sub">
                        {stats.postQuizMax != null ? `${Math.round((stats.quizAvg / stats.postQuizMax) * 100)}% • ${stats.lowUnderstanding.length} below ${Math.round(stats.postQuizMax * THRESHOLDS.QUIZ_SCORE_RATIO)}/${stats.postQuizMax}` : 'No post-quiz assigned'}
                      </div>
                    </div>
                    <div className="kpi">
                      <div className="label">Learning gain</div>
                      <div className="value">
                        {stats.preQuizMax != null && stats.postQuizMax != null
                          ? `${stats.quizAvg - stats.preQuizAvg >= 0 ? '+' : ''}${fmt(stats.quizAvg - stats.preQuizAvg, 2)}`
                          : 'N/A'}
                      </div>
                      <div className="sub">{stats.preQuizMax != null && stats.postQuizMax != null ? 'Pre-quiz → Post-quiz (attempters only)' : 'Need both quizzes'}</div>
                    </div>
                  </div>

                  <div className="split">
                    <div className="mini">
                      <h2 style={{ margin: '0 0 8px 0' }}>
                        Engagement by phase <span className="hint">Averages</span>
                      </h2>
                      <BarRow label="Pre: Videos" valueText={`${fmt(stats.preVideoAvg, 1)} min avg`} frac={clamp(stats.preVideoAvg / 20, 0, 1)} />
                      <BarRow label="Pre: PDFs" valueText={`${fmt(stats.prePdfAvg, 0)}% avg`} frac={clamp(stats.prePdfAvg / 100, 0, 1)} />
                      {data?.step?.resources?.pre?.submissions && data.step.resources.pre.submissions.totalStudents > 0 && (
                        <BarRow
                          label="Pre: AI Submissions"
                          valueText={`${data.step.resources.pre.submissions.studentsSubmitted} of ${data.step.resources.pre.submissions.totalStudents} submitted`}
                          frac={data.step.resources.pre.submissions.studentsSubmitted / data.step.resources.pre.submissions.totalStudents}
                        />
                      )}
                      <BarRow
                        label="In-class: Participation"
                        valueText={`${fmt(stats.inHandsAvg, 1)} hands-up avg`}
                        frac={clamp(stats.inHandsAvg / 7, 0, 1)}
                      />
                      <BarRow
                        label="Post: Practice"
                        valueText={`${fmt(stats.postPracticeAvg, 1)} min avg`}
                        frac={clamp(stats.postPracticeAvg / 30, 0, 1)}
                      />
                    </div>
                    {stats.quizItemAnalysis.map((qa, qi) => (
                      <div className="mini" key={qi}>
                        <h2 style={{ margin: '0 0 8px 0' }}>
                          {qa.phase === 'pre' ? 'Pre' : qa.phase === 'in' ? 'In-Class' : 'Post'} Quiz ({qa.items.length} questions) <span className="hint">Item analysis</span>
                        </h2>
                        {qa.items.map(it => (
                          <BarRow
                            key={it.q}
                            label={it.concept ? `Q${it.q} • ${it.concept}` : `Q${it.q}`}
                            valueText={`${Math.round(it.pct * 100)}% correct${it.note ? ` • ${it.note}` : ''}`}
                            frac={it.pct}
                          />
                        ))}
                        {qa.items.length > 1 && (
                          <div className="row" style={{ marginTop: '10px' }}>
                            <span className="tag bad">Hardest: Q{qa.hardest.q} ({Math.round(qa.hardest.pct * 100)}%)</span>
                            <span className="tag good">Easiest: Q{qa.easiest.q} ({Math.round(qa.easiest.pct * 100)}%)</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="insights">
                    {((stats.n - stats.lowPre.length) / stats.n) * 100 < 70 ? (
                      <Insight
                        dotClass="bad"
                        title="Pre-class completion is low. Start in-class with a 6-minute catch-up micro-lesson."
                        desc={`Only ${Math.round(
                          ((stats.n - stats.lowPre.length) / stats.n) * 100
                        )}% look prepared. Keep the first guided problem very similar to the pre videos, then move to the main task.`}
                      />
                    ) : (
                      <Insight
                        dotClass="good"
                        title="Most students are prepared. Start in-class with an application problem."
                        desc={`${Math.round(
                          ((stats.n - stats.lowPre.length) / stats.n) * 100
                        )}% look prepared. Begin with a single high-context application and ask students to justify their approach.`}
                      />
                    )}

                    <Insight
                      dotClass={stats.hardest.pct < 0.55 ? 'warn' : 'good'}
                      title={`Misconception signal: ${stats.hardest.concept} (Q${stats.hardest.q}).`}
                      desc={`Q${stats.hardest.q} had ${Math.round(
                        stats.hardest.pct * 100
                      )}% correct. Plan a 3-step explanation with clear examples.`}
                    />

                    {stats.atRisk.length > 0 ? (
                      <Insight
                        dotClass="bad"
                        title={`Intervention list: ${stats.atRisk.length} at-risk students need a targeted support path.`}
                        desc={`At-risk means low pre engagement + quiz under ${THRESHOLDS.QUIZ_SCORE_ABS}/${stats.postQuizMax ?? '?'}. Suggested: quick check-in + pair them with high performers. Examples: ${stats.atRisk
                          .slice(0, 5)
                          .map(s => s.name)
                          .join(', ')}${stats.atRisk.length > 5 ? '…' : ''}`}
                      />
                    ) : (
                      <Insight
                        dotClass="good"
                        title="No at-risk cluster detected."
                        desc="Continue with normal pacing. Use high performers as peer explainers for the hardest concept instead of a separate remedial track."
                      />
                    )}

                    <Insight
                      dotClass={(students.filter(s => s.post.practiceMin >= 15).length / stats.n) * 100 >= 70 ? 'good' : 'warn'}
                      title="Next step readiness (post practice)."
                      desc={
                        (students.filter(s => s.post.practiceMin >= 15).length / stats.n) * 100 >= 70
                          ? `${Math.round(
                              (students.filter(s => s.post.practiceMin >= 15).length / stats.n) * 100
                            )}% spent 15+ minutes on practice. You can introduce the next step with a short recap and move quickly to new content.`
                          : `${Math.round(
                              (students.filter(s => s.post.practiceMin >= 15).length / stats.n) * 100
                            )}% spent 15+ minutes on practice. Consider assigning a 5-minute recap before the next class, and re-run one key example as a warm-up.`
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'students' && (
              <div className="card">
                <h2>
                  Student-wise Performance
                  {studentFilter !== 'all' && (
                    <span className="filter-badge">
                      Filtered: {studentFilter === 'ready' ? 'Ready for next step' :
                                studentFilter === 'borderline' ? 'Borderline group' :
                                'Not ready without support'}
                      <button className="clear-filter" onClick={() => setStudentFilter('all')}>×</button>
                    </span>
                  )}
                </h2>
                <div style={{ overflowX: 'auto', marginTop: '16px' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Video (min)</th>
                        <th>PDF (%)</th>
                        <th>
                          Baseline Quiz
                          {data?.step?.resources?.pre?.quiz?.title && (
                            <span title={data.step.resources.pre.quiz.title} style={{ marginLeft: 4, cursor: 'help', color: 'var(--text-faint)', fontSize: 12 }}>&#9432;</span>
                          )}
                        </th>
                        <th>Participation</th>
                        <th>
                          End-line Quiz
                          {data?.step?.resources?.post?.quiz?.title && (
                            <span title={data.step.resources.post.quiz.title} style={{ marginLeft: 4, cursor: 'help', color: 'var(--text-faint)', fontSize: 12 }}>&#9432;</span>
                          )}
                        </th>
                        <th>Learning Gain</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students
                        .filter(student => {
                          if (studentFilter === 'all') return true;
                          const isReady = student.quiz.score >= 4 && student.post.practiceMin >= 15;
                          const isNotReady = student.quiz.score < THRESHOLDS.QUIZ_SCORE_ABS;
                          const isBorderline = !isReady && !isNotReady;

                          if (studentFilter === 'ready') return isReady;
                          if (studentFilter === 'notReady') return isNotReady;
                          if (studentFilter === 'borderline') return isBorderline;
                          return true;
                        })
                        .map(student => {
                          const gain = student.quiz.score - student.pre.preQuizScore;
                          const gainClass = gain >= 2 ? 'good' : gain >= 1 ? 'warn' : gain < 0 ? 'bad' : '';
                          return (
                            <tr key={student.id}>
                              <td>
                                <strong>{student.name}</strong>
                              </td>
                              <td>{student.pre.videoMin}</td>
                              <td>{student.pre.pdfPct}%</td>
                              <td>
                                <b>{stats.preQuizMax != null ? `${student.pre.preQuizScore}/${stats.preQuizMax}` : 'N/A'}</b>
                              </td>
                              <td>{student.in.participation}</td>
                              <td>
                                <b>{stats.postQuizMax != null ? `${student.quiz.score}/${stats.postQuizMax}` : 'N/A'}</b>
                              </td>
                              <td>
                                <span className={`tag ${gainClass}`}>
                                  {gain >= 0 ? '+' : ''}
                                  {gain}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* PRE-CLASS TAB */}
            {activeTab === 'pre' && (() => {
              const pick = (s: StudentData) => {
                const lowPre = (s.pre.videoMin < THRESHOLDS.VIDEO_MIN) || (s.pre.pdfPct < THRESHOLDS.PDF_PCT);
                const lowUnderstanding = s.quiz.score < THRESHOLDS.QUIZ_SCORE_ABS;
                const atRisk = lowPre && lowUnderstanding;
                const highPerf = (s.quiz.score === 5 && s.pre.videoMin >= 14 && s.pre.pdfPct >= 75);

                if (preSegment === 'all') return true;
                if (preSegment === 'atRisk') return atRisk;
                if (preSegment === 'lowEngagement') return lowPre;
                if (preSegment === 'highPerformers') return highPerf;
                return true;
              };

              const subset = students.filter(pick);
              const denom = subset.length || 1;

              const preVideoAvg = subset.reduce((sum, s) => sum + s.pre.videoMin, 0) / denom;
              const prePdfAvg = subset.reduce((sum, s) => sum + s.pre.pdfPct, 0) / denom;
              const preQuizAvg = subset.reduce((sum, s) => sum + s.pre.preQuizScore, 0) / denom;

              const prepared = subset.filter(s => s.pre.videoMin >= 12 && s.pre.pdfPct >= 60).length;
              const borderline = subset.filter(s => !(s.pre.videoMin < THRESHOLDS.VIDEO_MIN || s.pre.pdfPct < THRESHOLDS.PDF_PCT) && !(s.pre.videoMin >= 12 && s.pre.pdfPct >= 60)).length;
              const unprepared = subset.filter(s => (s.pre.videoMin < THRESHOLDS.VIDEO_MIN || s.pre.pdfPct < THRESHOLDS.PDF_PCT)).length;

              const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
              const videoFrac = clamp(preVideoAvg / 20, 0, 1);
              const pdfFrac = clamp(prePdfAvg / 100, 0, 1);
              const quizFrac = stats.preQuizMax != null ? clamp(preQuizAvg / stats.preQuizMax, 0, 1) : 0;

              const segLabel = preSegment === 'all' ? 'All students' :
                               preSegment === 'atRisk' ? 'At-risk' :
                               preSegment === 'lowEngagement' ? 'Low engagement' : 'High performers';

              return (
                <>
                <div className="card">
                  <h2>
                    Pre-class readiness
                    <span className="hint">Use this to adapt your in-class delivery • Segment: {segLabel} (n={subset.length})</span>
                  </h2>

                  <div className="controls">
                    <div className="field">
                      <label htmlFor="preFocus">Focus</label>
                      <select id="preFocus" value={preFocus} onChange={(e) => setPreFocus(e.target.value)}>
                        <option value="all">All pre resources</option>
                        <option value="video">Video only</option>
                        <option value="pdf">PDF only</option>
                        <option value="quiz">Pre-check quiz only</option>
                      </select>
                    </div>
                    <div className="field">
                      <label htmlFor="preSegment">Segment</label>
                      <select id="preSegment" value={preSegment} onChange={(e) => setPreSegment(e.target.value)}>
                        <option value="all">All students</option>
                        <option value="atRisk">At-risk</option>
                        <option value="lowEngagement">Low engagement</option>
                        <option value="highPerformers">High performers</option>
                      </select>
                    </div>
                  </div>

                  <div className="two" style={{ marginTop: '12px' }}>
                    <div className="mini">
                      <h2 style={{ margin: '0 0 8px 0' }}>Pre resources <span className="hint">What they actually used</span></h2>
                      {(preFocus === 'all' || preFocus === 'video') && (
                        <div className="row" style={{ position: 'relative' }}>
                          <div>
                            <div className="name">Videos</div>
                            <div className="meta">{fmt(preVideoAvg, 1)} min avg (of 20 min)</div>
                          </div>
                          <div className="bar"><span style={{ width: `${videoFrac * 100}%` }}></span></div>
                          <button onClick={() => openDebug('video', 'Video Watch Time')} style={{ position: 'absolute', top: 0, right: 0, fontSize: '10px', padding: '2px 6px', cursor: 'pointer', background: 'var(--surface-sunken)', border: '1px solid var(--border-strong)', borderRadius: '4px' }}>Details</button>
                        </div>
                      )}
                      {(preFocus === 'all' || preFocus === 'pdf') && (
                        <div className="pdf-section">
                          <div className="row" style={{ position: 'relative', marginBottom: '4px' }}>
                            <div>
                              <div className="name">
                                PDFs
                                <span className="info-icon" onClick={() => setShowPdfInfo(true)} title="Click for details">ⓘ</span>
                              </div>
                              <div className="meta">{fmt(prePdfAvg, 0)}% avg completion</div>
                            </div>
                            <button onClick={() => openDebug('pdf', 'PDF Completion')} style={{ position: 'absolute', top: 0, right: 0, fontSize: '10px', padding: '2px 6px', cursor: 'pointer', background: 'var(--surface-sunken)', border: '1px solid var(--border-strong)', borderRadius: '4px' }}>Details</button>
                          </div>
                          {/* Per-PDF breakdown */}
                          {data?.step?.resources?.pre?.pdfResources?.map((pdf: any) => (
                            <div key={pdf.id} className="pdf-resource-row">
                              <div className="pdf-name">{pdf.name} {pdf.totalPages > 0 && `(${pdf.totalPages} pages)`}</div>
                              <div className="pdf-meta">
                                {pdf.studentsAccessed} of {pdf.totalStudents} students opened
                                {pdf.studentsAccessed > 0 && pdf.avgPagesRead > 0 && ` • Avg reached page ${Math.round(pdf.avgPagesRead)}`}
                              </div>
                              <div className="bar">
                                <span style={{ width: `${pdf.totalStudents > 0 ? (pdf.studentsAccessed / pdf.totalStudents) * 100 : 0}%` }}></span>
                              </div>
                              {pdf.studentsAccessed === 0 && (
                                <div className="warning-tag">⚠️ No engagement yet</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {(preFocus === 'all' || preFocus === 'quiz') && (
                        <div className="row" style={{ position: 'relative' }}>
                          <div>
                            <div className="name">Pre-check quiz</div>
                            <div className="meta">{stats.preQuizMax != null ? `${fmt(preQuizAvg, 2)}/${stats.preQuizMax} avg` : 'N/A'}</div>
                          </div>
                          <div className="bar"><span style={{ width: `${quizFrac * 100}%` }}></span></div>
                          <button onClick={() => openDebug('quiz', 'Pre-check Quiz')} style={{ position: 'absolute', top: 0, right: 0, fontSize: '10px', padding: '2px 6px', cursor: 'pointer', background: 'var(--surface-sunken)', border: '1px solid var(--border-strong)', borderRadius: '4px' }}>Details</button>
                        </div>
                      )}
                      {/* AI Submissions row */}
                      {(preFocus === 'all') && data?.step?.resources?.pre?.submissions && (
                        <div className="row" style={{ position: 'relative' }}>
                          <div>
                            <div className="name">AI Submissions</div>
                            <div className="meta">
                              {data.step.resources.pre.submissions.studentsSubmitted > 0 ? (
                                <>
                                  {data.step.resources.pre.submissions.studentsSubmitted} submitted
                                  {data.step.resources.pre.submissions.gradedCount > 0 && (
                                    <> • {data.step.resources.pre.submissions.gradedCount} graded
                                      {data.step.resources.pre.submissions.avgScore !== null && ` (avg ${data.step.resources.pre.submissions.avgScore}%)`}
                                    </>
                                  )}
                                </>
                              ) : (
                                <span style={{ color: 'var(--text-faint)' }}>No submissions yet</span>
                              )}
                            </div>
                          </div>
                          <div className="bar">
                            <span style={{ width: `${data.step.resources.pre.submissions.totalStudents > 0 ? (data.step.resources.pre.submissions.studentsSubmitted / data.step.resources.pre.submissions.totalStudents) * 100 : 0}%` }}></span>
                          </div>
                          <button onClick={() => openDebug('submissions', 'AI Submissions')} style={{ position: 'absolute', top: 0, right: 0, fontSize: '10px', padding: '2px 6px', cursor: 'pointer', background: 'var(--surface-sunken)', border: '1px solid var(--border-strong)', borderRadius: '4px' }}>Details</button>
                        </div>
                      )}
                    </div>
                    <div className="mini">
                      <h2 style={{ margin: '0 0 8px 0' }}>Ready for in-class? <span className="hint">Signals</span></h2>
                      <div className="row">
                        <div>
                          <div className="name">Prepared</div>
                          <div className="meta">{prepared} students</div>
                        </div>
                        <div className="bar"><span style={{ width: `${(prepared / denom) * 100}%` }}></span></div>
                      </div>
                      <div className="row">
                        <div>
                          <div className="name">Borderline</div>
                          <div className="meta">{borderline} students</div>
                        </div>
                        <div className="bar"><span style={{ width: `${(borderline / denom) * 100}%` }}></span></div>
                      </div>
                      <div className="row">
                        <div>
                          <div className="name">Unprepared</div>
                          <div className="meta">{unprepared} students</div>
                        </div>
                        <div className="bar"><span style={{ width: `${(unprepared / denom) * 100}%` }}></span></div>
                      </div>
                      <ul className="threshold-list" style={{ marginTop: '12px' }}>
                        <li><strong>Low engagement:</strong> video &lt; {THRESHOLDS.VIDEO_MIN}m or pdf &lt; {THRESHOLDS.PDF_PCT}%</li>
                        <li><strong>Low understanding:</strong> quiz &lt; {THRESHOLDS.QUIZ_SCORE_ABS}/{stats.postQuizMax ?? '?'}</li>
                        <li><strong>At-risk:</strong> both above</li>
                      </ul>
                    </div>
                  </div>
                </div>
                {renderQuizDonuts('pre')}
                <div className="card">
                  <div className="mini" style={{ marginTop: '0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h2 style={{ margin: '0' }}>In-class tweaks (based on pre-class)</h2>
                      <button
                        className="btn-generate-ai"
                        onClick={() => stats && generateAITweaks(stats, unprepared, denom)}
                        disabled={aiTweaksLoading || !stats}
                        title="Generate personalized recommendations using AI"
                      >
                        {aiTweaksLoading ? (
                          <>
                            <span className="spinner-small"></span>
                            Generating...
                          </>
                        ) : (
                          <>✨ Generate with AI</>
                        )}
                      </button>
                    </div>

                    {aiTweaksError && (
                      <div className="ai-error-message">
                        {aiTweaksError}
                        <button onClick={() => setAiTweaksError(null)} style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
                      </div>
                    )}

                    {aiOverallAssessment && aiTweaks && (
                      <div className="ai-assessment">
                        <strong>AI Assessment:</strong> {aiOverallAssessment}
                      </div>
                    )}

                    <div className="insights">
                      {aiTweaks ? (
                        // Render AI-generated tweaks
                        aiTweaks.map((tweak) => (
                          <div key={tweak.id} className={`insight ai-insight priority-${tweak.priority}`}>
                            <div className={`dot ${tweak.priority === 'high' ? 'warn' : tweak.priority === 'medium' ? 'info' : 'good'}`}></div>
                            <div>
                              <div className="t">
                                {tweak.title}
                                {tweak.timeEstimate && <span className="time-badge">{tweak.timeEstimate}</span>}
                              </div>
                              <div className="d">{tweak.description}</div>
                            </div>
                          </div>
                        ))
                      ) : (
                        // Fallback to hardcoded tweaks
                        <>
                          {unprepared / denom > 0.35 ? (
                            <div className="insight">
                              <div className="dot warn"></div>
                              <div>
                                <div className="t">Add a 4-minute guided recap before group work.</div>
                                <div className="d">In the selected segment, {Math.round((unprepared / denom) * 100)}% are unprepared. Show a single clean diagram and restate the frame-of-reference rule before you ask them to solve.</div>
                              </div>
                            </div>
                          ) : (
                            <div className="insight">
                              <div className="dot good"></div>
                              <div>
                                <div className="t">You can start with a diagnostic question instead of a recap.</div>
                                <div className="d">Ask one question that forces students to define the observer first. Use responses to decide whether you need a recap.</div>
                              </div>
                            </div>
                          )}
                          <div className="insight">
                            <div className={`dot ${stats && stats.hardest.pct < 0.55 ? 'warn' : 'good'}`}></div>
                            <div>
                              <div className="t">Target the hard concept early: {stats?.hardest.concept}.</div>
                              <div className="d">During in-class, spend 6-8 minutes on the hardest concept using: a) one diagram, b) one non-example, c) one quick check.</div>
                            </div>
                          </div>
                          <div className="insight">
                            <div className="dot warn"></div>
                            <div>
                              <div className="t">Pairing strategy</div>
                              <div className="d">Pair low-engagement students with an Explainer for the first guided problem. Then rotate roles so the same students are not always passive.</div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                </>
              );
            })()}

            {/* IN-CLASS TAB */}
            {activeTab === 'in' && (() => {
              // Participation - only count students with REAL teacher-entered data
              const high = students.filter(s => s.in.participation === 'High').length;
              const medium = students.filter(s => s.in.participation === 'Medium').length;
              const low = students.filter(s => s.in.participation === 'Low').length;
              const participationNotTracked = students.filter(s => s.in.participation === null).length;

              // Hands-up average - only from students with engagement records
              const studentsWithHands = students.filter(s => s.in.handsUp !== null);
              const avgHands = studentsWithHands.length > 0
                ? studentsWithHands.reduce((sum, s) => sum + (s.in.handsUp || 0), 0) / studentsWithHands.length
                : 0;
              const handsTrackedCount = studentsWithHands.length;

              // Poll average - only from students with poll data
              const studentsWithPolls = students.filter(s => s.in.pollCorrect !== null);
              const avgPoll = studentsWithPolls.length > 0
                ? studentsWithPolls.reduce((sum, s) => sum + (s.in.pollCorrect || 0), 0) / studentsWithPolls.length
                : 0;
              const pollTrackedCount = studentsWithPolls.length;

              // Roles - only count students with REAL teacher-entered data
              const solvers = students.filter(s => s.in.groupRole === 'Solver' || s.in.groupRole === 'Explainer').length;
              const passive = students.filter(s => s.in.groupRole === 'Listener' || s.in.groupRole === 'Absent').length;
              const roleNotTracked = students.filter(s => s.in.groupRole === null).length;

              // Quiet but struggling - only count students who ACTUALLY have handsUp === 0 (tracked, not null)
              const quietStruggle = students.filter(s => s.quiz.score < THRESHOLDS.QUIZ_SCORE_ABS && s.in.handsUp === 0).length;

              const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

              const riskScore = (s: StudentData) => {
                let risk = 0;
                if (s.pre.videoMin < THRESHOLDS.VIDEO_MIN) risk += 20;
                if (s.pre.pdfPct < THRESHOLDS.PDF_PCT) risk += 20;
                if (s.pre.preQuizScore < 2) risk += 15;
                if (s.quiz.score < THRESHOLDS.QUIZ_SCORE_ABS) risk += 25;
                // Only add risk for LOW participation, not for null (untracked)
                if (s.in.participation === 'Low') risk += 20;
                return risk;
              };

              const interventions = students
                .map(s => ({ s, risk: riskScore(s) }))
                .sort((a, b) => b.risk - a.risk)
                .slice(0, 8);

              return (
                <>
                <div className="card">
                  <div className="in-class-header">
                    <h2>
                      In-class engagement
                      <span className="hint">What happened during live time</span>
                    </h2>
                    <div className="in-class-actions">
                      <button
                        className="btn-in-class flash"
                        onClick={() => setShowFlashQuiz(true)}
                        title="Flash a pre-quiz question to the class"
                      >
                        Flash Question
                      </button>
                      <button
                        className="btn-in-class flash"
                        onClick={() => setShowWhiteboard(true)}
                        title="Open whiteboard for live topic explanation"
                      >
                        Whiteboard
                      </button>
                      <button
                        className="btn-in-class btn-form-groups"
                        onClick={() => setShowGroupFormation(true)}
                        title="Form student groups for AI-assisted case discussions"
                      >
                        Form Groups
                      </button>
                      <button
                        className="btn-in-class btn-case-discussion"
                        onClick={() => setShowCaseDiscussion(true)}
                        title="Run AI-assisted case discussions — see which group needs you most"
                      >
                        Case Discussions
                      </button>
                      <button
                        className="btn-in-class btn-case-discussion"
                        onClick={() =>
                          window.open(
                            `/case-studies?view=responses&from=class-review&stepId=${stepId}&batchId=${batchId}&lessonPlanId=${lessonPlanId}`,
                            '_blank',
                          )
                        }
                        title="Review and correct students' self-paced case-study responses"
                      >
                        Case Responses
                      </button>
                      <button
                        className={`btn-in-class tracker ${showEngagementTracker ? 'active' : ''}`}
                        onClick={() => {
                          const wasVisible = showEngagementTracker;
                          setShowEngagementTracker(!wasVisible);
                          if (wasVisible) {
                            // Refresh data after hiding tracker so Live Signals reflects updates
                            setTimeout(() => loadAnalyticsData(), 500);
                          }
                        }}
                        title="Open live engagement tracker"
                      >
                        {showEngagementTracker ? 'Hide Tracker' : 'Live Tracker'}
                      </button>
                    </div>
                  </div>

                  {showEngagementTracker && (
                    <div style={{ marginBottom: '16px' }}>
                      <LiveEngagementTracker
                        stepId={Number(stepId)}
                        batchId={Number(batchId)}
                        students={students}
                        onDataRefresh={loadAnalyticsData}
                        onFlashQuizClick={() => setShowFlashQuiz(true)}
                        onInClassQuizClick={() => alert('In-class quiz management coming soon!')}
                      />
                    </div>
                  )}

                  <div className="mini signals-compact" style={{ marginTop: '12px' }}>
                    {/* Participation */}
                    <div className="sc-section">
                      <div className="sc-row">
                        <span className="sc-label">Participation</span>
                        <span className="sc-chips">
                          <span className="sc-chip good">High: {high}</span>
                          <span className="sc-chip warn">Med: {medium}</span>
                          <span className="sc-chip bad">Low: {low}</span>
                          {participationNotTracked > 0 && (
                            <span className="sc-chip muted">Not tracked: {participationNotTracked}</span>
                          )}
                        </span>
                      </div>
                      <div className="sc-row">
                        <span className="sc-label">Hands-up</span>
                        <span className="sc-val">
                          {handsTrackedCount > 0 ? `${fmt(avgHands, 1)} avg` : 'No data'}
                          {handsTrackedCount > 0 && handsTrackedCount < stats.n && (
                            <span className="sc-tracked">({handsTrackedCount} tracked)</span>
                          )}
                        </span>
                        <div className="bar sc-bar"><span style={{ width: `${handsTrackedCount > 0 ? clamp(avgHands / 7, 0, 1) * 100 : 0}%` }}></span></div>
                      </div>
                      <div className="sc-row">
                        <span className="sc-label">Quick polls</span>
                        <span className="sc-val">
                          {pollTrackedCount > 0 ? `${fmt(avgPoll, 1)} correct avg` : 'No data'}
                          {pollTrackedCount > 0 && pollTrackedCount < stats.n && (
                            <span className="sc-tracked">({pollTrackedCount} tracked)</span>
                          )}
                        </span>
                        <div className="bar sc-bar"><span style={{ width: `${pollTrackedCount > 0 ? clamp(avgPoll / 3, 0, 1) * 100 : 0}%` }}></span></div>
                      </div>
                    </div>

                    {/* Roles */}
                    <div className="sc-section">
                      <div className="sc-row">
                        <span className="sc-label">Roles</span>
                        <span className="sc-chips">
                          <span className="sc-chip">Solvers/Explainers: {solvers}</span>
                          <span className="sc-chip">Listeners/Absent: {passive}</span>
                          {roleNotTracked > 0 && (
                            <span className="sc-chip muted">Not tracked: {roleNotTracked}</span>
                          )}
                        </span>
                      </div>
                      <div className="sc-row">
                        <span className="sc-label">Active share</span>
                        <span className="sc-val">
                          {(solvers + passive) > 0 ? `${Math.round((solvers / (solvers + passive)) * 100)}%` : 'No data'}
                        </span>
                        <div className="bar sc-bar"><span style={{ width: `${(solvers + passive) > 0 ? clamp(solvers / (solvers + passive), 0, 1) * 100 : 0}%` }}></span></div>
                      </div>
                    </div>

                    {/* Question Signals */}
                    <div className="sc-section sc-section-last">
                      <div className="sc-row">
                        <span className="sc-label">Signals</span>
                        <span className="sc-chips">
                          <span className="sc-chip bad">Quiet struggling: {handsTrackedCount > 0 ? quietStruggle : '-'}</span>
                          <span className="sc-chip bad">At-risk: {stats.atRisk.length}</span>
                        </span>
                      </div>
                      <div className="sc-row">
                        <span className="sc-label">Quiet share</span>
                        <span className="sc-val">
                          {handsTrackedCount > 0 ? `${Math.round((quietStruggle / handsTrackedCount) * 100)}%` : 'No data'}
                        </span>
                        <div className="bar sc-bar"><span style={{ width: `${handsTrackedCount > 0 ? clamp(quietStruggle / handsTrackedCount, 0, 1) * 100 : 0}%` }}></span></div>
                      </div>
                    </div>
                  </div>
                </div>
                {renderQuizDonuts('in')}
                <div className="card">
                  <div className="mini" style={{ marginTop: '0' }}>
                    <h2 style={{ margin: '0' }}>Intervention list (during class)</h2>
                    <div className="insights">
                      {interventions.map(({ s, risk }) => {
                        const lowPre = (s.pre.videoMin < THRESHOLDS.VIDEO_MIN) || (s.pre.pdfPct < THRESHOLDS.PDF_PCT);
                        const lowUnderstanding = s.quiz.score < THRESHOLDS.QUIZ_SCORE_ABS;
                        const dotClass = (lowPre && lowUnderstanding) ? 'bad' : (lowUnderstanding || lowPre) ? 'warn' : 'good';
                        const action = (lowPre && lowUnderstanding)
                          ? 'Seat near front + quick 1:1 check; pair with Explainer'
                          : (lowUnderstanding
                            ? `Ask them to explain Q${stats.hardest.q} reasoning`
                            : 'Prompt them to define observer before solving');

                        return (
                          <div key={s.id} className="insight">
                            <div className={`dot ${dotClass}`}></div>
                            <div>
                              <div className="t">{s.name} ({s.id}) • Risk {risk}/100</div>
                              <div className="d">{action}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                </>
              );
            })()}

            {/* POST-CLASS TAB */}
            {activeTab === 'post' && (() => {
              const n = stats.n;
              const practiced15 = students.filter(s => s.post.practiceMin >= 15).length;
              const recap = students.filter(s => s.post.recapViewed).length;
              const reflection = students.filter(s => s.post.reflection).length;

              const improved = students.filter(s => s.quiz.score - s.pre.preQuizScore >= 2).length;
              const smallGain = students.filter(s => s.quiz.score - s.pre.preQuizScore === 1).length;
              const noGain = students.filter(s => s.quiz.score - s.pre.preQuizScore <= 0).length;

              const ready = students.filter(s => s.quiz.score >= 4 && s.post.practiceMin >= 15).length;
              const borderline = students.filter(s => s.quiz.score === THRESHOLDS.QUIZ_SCORE_ABS || (s.quiz.score >= 4 && s.post.practiceMin < 15)).length;
              const notReady = students.filter(s => s.quiz.score < THRESHOLDS.QUIZ_SCORE_ABS).length;

              return (
                <>
                <div className="card">
                  <h2>
                    Post-class reinforcement
                    <span className="hint">Who practiced, who revised, who still needs support</span>
                  </h2>

                  <div className="two" style={{ marginTop: '12px' }}>
                    <div className="mini">
                      <h2 style={{ margin: '0 0 8px 0' }}>Post activities <span className="hint">Completion</span> <span className="info-icon" onClick={() => setShowPostInfo('activities')} title="How is this calculated?">ⓘ</span></h2>
                      <div className="row">
                        <div>
                          <div className="name">Practice (15+ min)</div>
                          <div className="meta">{practiced15}/{n} students</div>
                        </div>
                        <div className="bar"><span style={{ width: `${(practiced15 / n) * 100}%` }}></span></div>
                      </div>
                      <div className="row">
                        <div>
                          <div className="name">Recap video</div>
                          <div className="meta">{recap}/{n} students</div>
                        </div>
                        <div className="bar"><span style={{ width: `${(recap / n) * 100}%` }}></span></div>
                      </div>
                      <div className="row">
                        <div>
                          <div className="name">Reflection</div>
                          <div className="meta">{reflection}/{n} students</div>
                        </div>
                        <div className="bar"><span style={{ width: `${(reflection / n) * 100}%` }}></span></div>
                      </div>
                    </div>
                    <div className="mini">
                      <h2 style={{ margin: '0 0 8px 0' }}>Learning gains <span className="hint">Quiz vs pre-check</span> <span className="info-icon" onClick={() => setShowPostInfo('gains')} title="How is this calculated?">ⓘ</span></h2>
                      <div className="row">
                        <div>
                          <div className="name">Improved (&gt;= +2)</div>
                          <div className="meta">{improved} students</div>
                        </div>
                        <div className="bar"><span style={{ width: `${(improved / n) * 100}%` }}></span></div>
                      </div>
                      <div className="row">
                        <div>
                          <div className="name">Small gain (+1)</div>
                          <div className="meta">{smallGain} students</div>
                        </div>
                        <div className="bar"><span style={{ width: `${(smallGain / n) * 100}%` }}></span></div>
                      </div>
                      <div className="row">
                        <div>
                          <div className="name">No gain</div>
                          <div className="meta">{noGain} students</div>
                        </div>
                        <div className="bar"><span style={{ width: `${(noGain / n) * 100}%` }}></span></div>
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const preQuizzes = (data?.quizPerformance || []).filter((qp: QuizPerformanceEntry) => qp.phase === 'pre');
                    const postQuizzes = (data?.quizPerformance || []).filter((qp: QuizPerformanceEntry) => qp.phase === 'post');
                    if (preQuizzes.length === 0 && postQuizzes.length === 0) return null;
                    const denom = n || 1;
                    const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
                    return (
                      <div className="two" style={{ marginTop: '16px' }}>
                        <div className="mini">
                          <h2 style={{ margin: '0 0 8px 0' }}>Pre-check <span className="hint">Baseline</span> <span className="info-icon" onClick={() => setShowPostInfo('scores')} title="How is this calculated?">ⓘ</span></h2>
                          {preQuizzes.map((pq: QuizPerformanceEntry) => {
                            const avg = students.reduce((sum, s) => sum + s.pre.preQuizScore, 0) / denom;
                            const maxMarks = pq.totalMarks || pq.blueprint.length;
                            const frac = maxMarks > 0 ? clamp(avg / maxMarks, 0, 1) : 0;
                            return (
                              <div className="row" key={pq.testId}>
                                <div>
                                  <div className="name">{pq.testName}</div>
                                  <div className="meta">{avg.toFixed(2)}/{maxMarks} avg</div>
                                </div>
                                <div className="bar"><span style={{ width: `${frac * 100}%` }}></span></div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mini">
                          <h2 style={{ margin: '0 0 8px 0' }}>Post-check <span className="hint">Endline</span> <span className="info-icon" onClick={() => setShowPostInfo('scores')} title="How is this calculated?">ⓘ</span></h2>
                          {postQuizzes.map((pq: QuizPerformanceEntry) => {
                            const avg = students.reduce((sum, s) => sum + s.quiz.score, 0) / denom;
                            const maxMarks = pq.totalMarks || pq.blueprint.length;
                            const frac = maxMarks > 0 ? clamp(avg / maxMarks, 0, 1) : 0;
                            return (
                              <div className="row" key={pq.testId}>
                                <div>
                                  <div className="name">{pq.testName}</div>
                                  <div className="meta">{avg.toFixed(2)}/{maxMarks} avg</div>
                                </div>
                                <div className="bar"><span style={{ width: `${frac * 100}%` }}></span></div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                {renderQuizDonuts('post')}
                <div className="card">
                  <div className="mini" style={{ marginTop: '0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h2 style={{ margin: '0' }}>Next-step readiness (based on post)</h2>
                      <button
                        className="btn-generate-ai"
                        onClick={generatePostClassRecommendations}
                        disabled={postClassRecsLoading}
                        style={{
                          background: 'var(--gradient-brand)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 14px',
                          fontSize: '13px',
                          fontWeight: 500,
                          cursor: postClassRecsLoading ? 'wait' : 'pointer',
                          opacity: postClassRecsLoading ? 0.7 : 1,
                        }}
                      >
                        {postClassRecsLoading ? 'Generating...' : 'Generate with AI'}
                      </button>
                    </div>

                    {postClassRecsError && (
                      <div style={{ color: 'var(--status-danger)', marginBottom: '12px', fontSize: '13px' }}>
                        {postClassRecsError}
                      </div>
                    )}

                    {postClassRecs ? (
                      <div className="ai-recommendations">
                        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                          <div style={{ fontSize: '13px', color: '#0369a1', marginBottom: '8px' }}>
                            <strong>AI Assessment:</strong> {postClassRecs.overallAssessment}
                          </div>
                          <div style={{ fontSize: '13px', color: '#0369a1' }}>
                            <strong>Next Class Focus:</strong> {postClassRecs.nextClassFocus}
                          </div>
                        </div>
                        <div className="insights">
                          {postClassRecs.recommendations.map((rec) => (
                            <div key={rec.id} className="insight">
                              <div className={`dot ${rec.priority === 'high' ? 'bad' : rec.priority === 'medium' ? 'warn' : 'good'}`}></div>
                              <div>
                                <div className="t">
                                  {rec.title}
                                  {rec.targetGroup && (
                                    <span style={{
                                      marginLeft: '8px',
                                      fontSize: '11px',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      background: rec.targetGroup === 'notReady' ? '#fee2e2' :
                                                  rec.targetGroup === 'borderline' ? '#fef3c7' :
                                                  rec.targetGroup === 'ready' ? '#dcfce7' : '#f3f4f6',
                                      color: rec.targetGroup === 'notReady' ? '#991b1b' :
                                             rec.targetGroup === 'borderline' ? '#92400e' :
                                             rec.targetGroup === 'ready' ? '#166534' : '#374151',
                                    }}>
                                      {rec.targetGroup}
                                    </span>
                                  )}
                                </div>
                                <div className="d">{rec.description}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="insights">
                        <div
                          className="insight clickable"
                          onClick={() => { setActiveTab('students'); setStudentFilter('ready'); }}
                        >
                          <div className={`dot ${ready / n >= 0.55 ? 'good' : 'warn'}`}></div>
                          <div>
                            <div className="t">Ready for next step <span className="click-hint">→ View students</span></div>
                            <div className="d">{ready}/{n} show strong understanding (&gt;=4/{stats.postQuizMax ?? '?'}) AND did enough practice. These students can handle a faster pace or a challenge problem next class.</div>
                          </div>
                        </div>
                        <div
                          className="insight clickable"
                          onClick={() => { setActiveTab('students'); setStudentFilter('borderline'); }}
                        >
                          <div className="dot warn"></div>
                          <div>
                            <div className="t">Borderline group <span className="click-hint">→ View students</span></div>
                            <div className="d">{borderline}/{n} are borderline. Begin the next class with one warm-up that targets Q{stats.hardest.q} and ask them to explain the observer choice out loud.</div>
                          </div>
                        </div>
                        {notReady > 0 && (() => {
                          const names = students
                            .filter(s => s.quiz.score < THRESHOLDS.QUIZ_SCORE_ABS)
                            .slice(0, 6)
                            .map(s => s.name)
                            .join(', ') + (notReady > 6 ? '…' : '');

                          return (
                            <div
                              className="insight clickable"
                              onClick={() => { setActiveTab('students'); setStudentFilter('notReady'); }}
                            >
                              <div className="dot bad"></div>
                              <div>
                                <div className="t">Not ready without support <span className="click-hint">→ View students</span></div>
                                <div className="d">{notReady}/{n} need a short remedial path before you build on this concept. Examples: {names}. Suggested: assign recap + 2 guided questions and re-check at the start of next class.</div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
                </>
              );
            })()}
          </main>
        </div>
      </div>

      {/* Flash Quiz Manager Modal */}
      <FlashQuizManager
        stepId={Number(stepId)}
        batchId={Number(batchId)}
        isOpen={showFlashQuiz}
        onClose={() => setShowFlashQuiz(false)}
        onQuizComplete={loadAnalyticsData}
      />

      {/* Whiteboard Manager Modal */}
      <WhiteboardManager
        stepId={Number(stepId)}
        batchId={Number(batchId)}
        isOpen={showWhiteboard}
        onClose={() => setShowWhiteboard(false)}
      />

      {/* Group Formation Modal (AI Case Discussions foundation) */}
      <GroupFormationModal
        stepId={Number(stepId)}
        batchId={Number(batchId)}
        isOpen={showGroupFormation}
        onClose={() => setShowGroupFormation(false)}
      />

      {/* AI Case Discussion Manager — Walk-Here-Next live triage */}
      <CaseDiscussionManager
        stepId={Number(stepId)}
        batchId={Number(batchId)}
        lessonPlanId={Number(lessonPlanId)}
        stepName={data?.step?.title || ""}
        isOpen={showCaseDiscussion}
        onClose={() => setShowCaseDiscussion(false)}
      />
    </div>
  );
};

export default ClassReviewDashboard;
